import { useEffect, useId, useState, type FormEvent } from "react";
import { useCreateAgentSession, useLlmCredentials } from "../../api/queries";
import { ApiError } from "../../api/errors";
import type { AgentSession } from "../../api/types";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { FormError } from "../../components/FormError";
import { Modal } from "../../components/Modal";
import "../../components/Form.css";
import "./develop.css";

interface StartDevelopDialogProps {
  open: boolean;
  /** The project the co-authored corpus is FOR — fixed by the page that
   * opened this dialog (CorpusPage's corpus tab, or ProjectSessionsTab's Sessions
   * tab — one component, two entry points), never picked here: develop is
   * reached from a project's own corpus, not a bare "start anywhere" form. */
  projectId: string;
  projectName: string;
  onClose: () => void;
  /** Called once the session is actually running server-side accepted (the
   * create call returned) — the caller navigates into the conversational
   * view. This dialog never navigates itself (S-15: it only reports what
   * actually happened). */
  onLaunched: (session: AgentSession) => void;
}

/** The exact substring `agentsession.Service.CreateDevelopSession` puts in
 * its refusal when no develop image is configured server-side (the founder
 * gate, ADR-0052 M6) — matched here so that specific, expected refusal gets
 * its own honest "not enabled" state instead of the generic error banner
 * every other validation failure falls through to. */
const DEVELOP_NOT_ENABLED_MARKER = "develop sessions are not enabled on this server";

function isDevelopNotEnabled(error: unknown): boolean {
  return error instanceof ApiError && error.message.includes(DEVELOP_NOT_ENABLED_MARKER);
}

/**
 * Starts a DEVELOP (corpus-authoring) session for a project (ADR-0052 M6):
 * `develop: true` + `projectId` on `createAgentSession` — the server pins
 * the curated develop agent image itself (no image field here, unlike
 * `StartBonnieSessionDialog`; see `CreateAgentSessionInput.develop`'s doc
 * comment for why the client never chooses one). Like a Bonnie session this
 * targets the developer's OWN enrolled local node; if none is enrolled the
 * create call fails and that failure renders here.
 */
export function StartDevelopDialog({ open, projectId, projectName, onClose, onLaunched }: StartDevelopDialogProps) {
  const labelId = useId();
  const credentialId = useId();
  const branchId = useId();
  const mutation = useCreateAgentSession();
  const { data: credentials } = useLlmCredentials();
  const [label, setLabel] = useState("");
  const [selectedCredential, setSelectedCredential] = useState("");
  const [branch, setBranch] = useState("");

  const credentialItems = credentials?.items ?? [];

  useEffect(() => {
    if (credentialItems.length === 1 && !selectedCredential) {
      setSelectedCredential(credentialItems[0].id);
    }
  }, [credentialItems, selectedCredential]);

  function handleClose() {
    setLabel("");
    setSelectedCredential("");
    setBranch("");
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submittedLabel = label.trim();
    mutation.mutate(
      {
        credentialId: selectedCredential,
        image: "",
        label: submittedLabel,
        develop: true,
        projectId,
        branch: branch.trim() || undefined,
      },
      {
        onSuccess: (created) => {
          onLaunched(created);
          handleClose();
        },
      },
    );
  }

  const notEnabled = isDevelopNotEnabled(mutation.error);

  return (
    <Modal open={open} title="Develop your app" onClose={handleClose} initialFocusId={notEnabled ? undefined : labelId}>
      {notEnabled ? (
        <div>
          <Banner tone="info" title="Develop isn't enabled on this server">
            This deployment hasn't configured a develop agent image yet. Ask an admin to set
            it up (<code>COMEBYE_DEVELOP_IMAGE</code>), then come back and try again.
          </Banner>
          <div className="form-actions">
            <Button type="button" variant="primary" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <p>
            Start a governed AI session that co-authors <strong>{projectName}</strong>'s corpus —
            vision, roadmap, decisions, governance — as a conversation. It drafts and validates on
            your own machine; nothing merges until you review and propose it.
          </p>

          <label className="form-field" htmlFor={labelId}>
            <span>Session label</span>
            <input
              id={labelId}
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              required
              maxLength={128}
              placeholder="e.g. First pass at the vision"
            />
          </label>

          <label className="form-field" htmlFor={credentialId}>
            <span>LLM credential</span>
            <select
              id={credentialId}
              value={selectedCredential}
              onChange={(e) => setSelectedCredential(e.target.value)}
              required
            >
              <option value="" disabled>
                Select a credential…
              </option>
              {credentialItems.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label} ({c.provider})
                </option>
              ))}
            </select>
            {credentialItems.length === 0 && (
              <span className="form-field__hint">No LLM credentials configured. Add one first.</span>
            )}
          </label>

          <label className="form-field" htmlFor={branchId}>
            <span>Branch (optional)</span>
            <input
              id={branchId}
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              maxLength={200}
              placeholder="Leave blank to let it name a branch for you"
            />
            <span className="form-field__hint">
              The session runs on its own git worktree for this branch, on your machine.
            </span>
          </label>

          {mutation.isError && mutation.error && (
            <FormError>{mutation.error.message || "Couldn't start a develop session."}</FormError>
          )}

          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={mutation.isPending || !label.trim() || !selectedCredential}
            >
              {mutation.isPending ? "Starting…" : "Start developing"}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
